import { createRoot } from "react-dom/client";
import { Component, type ReactNode } from "react";
import { initDatadog } from "./lib/datadog";
import { registerServiceWorker } from "@commander4/elements/lib/service-worker";
import "./lib/i18n"; // Initialize i18n before App import
import "./globals.css";

initDatadog();

/*
 * The worker is registered BEFORE `App` is imported, and the ordering is load-bearing.
 *
 * Auth contexts in these apps build their OpenAuth client at module scope from
 * `import.meta.env.VITE_AUTH_URL`, so a missing or malformed value throws while that module is still
 * evaluating — taking the whole entry down. A registration statement placed after the import never
 * runs, because static imports are hoisted above every statement regardless of where it sits.
 *
 * Reproduced in deploy-web: with `VITE_AUTH_URL` unset the page threw `Error: No issuer` and
 * `getRegistrations()` returned 0. The cost is specific — a misconfigured deploy loses the cached
 * shell that would otherwise have kept the previous good build usable.
 *
 * A dynamic import for `App` is what makes the ordering real rather than cosmetic.
 */
registerServiceWorker();

const App = (await import("./App.tsx")).default;

// Top-level error boundary — catches anything that slips past per-route boundaries,
// including provider errors and module-level crashes that React can surface.
/*
 * DELIBERATELY NOT TRANSLATED, and it is the one surface here that must not be.
 *
 * This boundary catches a crash during BOOT — including a crash in `./i18n` itself, which
 * is imported a few lines below. Calling `t()` here would mean the error screen depends on
 * the subsystem whose failure it exists to report, so a broken locale file would render a
 * blank page instead of "Application error". It is also a CLASS component, so a hook is
 * unavailable without `withTranslation`, which would add the same dependency.
 *
 * Two short strings in the one place where English-always is the safer guarantee.
 */
class RootErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "sans-serif", padding: "2rem", background: "#0f0f0f", color: "#e5e5e5" }}>
          <div style={{ maxWidth: 480, textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
            <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Application error</h1>
            <p style={{ color: "#888", fontSize: 13, marginBottom: 24, wordBreak: "break-word" }}>
              {this.state.error.message}
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{ padding: "8px 20px", borderRadius: 8, background: "#7c3aed", color: "#fff", border: "none", cursor: "pointer", fontSize: 14 }}
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

createRoot(document.getElementById("root")!).render(
  <RootErrorBoundary>
    <App />
  </RootErrorBoundary>
);
